<template>
  <div class="about-us">
    <UnifiedNavBar />
    
    <!-- 页面头部 -->
    <section class="page-header">
      <div class="container">
        <h1 class="page-title">关于我们</h1>
        <p class="page-subtitle">专业的工业互联网解决方案提供商，致力于为制造企业提供数字化转型和智能制造服务</p>
      </div>
    </section>

    <!-- 公司简介 -->
    <section class="company-intro">
      <div class="container">
        <div class="intro-content">
          <div class="intro-text">
            <h2 class="section-title">易博数字数字科技</h2>
            <p class="intro-description">
              易博数字数字科技有限公司成立于2018年，是一家专注于工业互联网、人工智能和数字化转型的高新技术企业。
              公司总部位于深圳，在北京、上海、广州等地设有分支机构，拥有一支由博士、硕士组成的高素质研发团队。
            </p>
            <p class="intro-description">
              我们致力于为制造企业提供全方位的数字化解决方案，通过先进的物联网技术、大数据分析和人工智能算法，
              帮助企业实现生产过程的智能化、管理的数字化和决策的科学化，推动传统制造业向智能制造转型升级。
            </p>
            <div class="company-stats">
              <div class="stat-item" v-for="stat in companyStats" :key="stat.id">
                <div class="stat-value">{{ stat.value }}</div>
                <div class="stat-label">{{ stat.label }}</div>
              </div>
            </div>
          </div>
          <div class="intro-image">
            <img src="https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Modern%20technology%20company%20office%20building%20with%20glass%20facade%20corporate%20headquarters%20professional%20business%20environment&image_size=landscape_4_3" alt="公司总部">
          </div>
        </div>
      </div>
    </section>

    <!-- 企业文化 -->
    <section class="company-culture">
      <div class="container">
        <h2 class="section-title">企业文化</h2>
        <p class="section-description">
          秉承"创新、专业、合作、共赢"的企业价值观，打造具有竞争力的企业文化
        </p>
        <div class="culture-grid">
          <div class="culture-card" v-for="culture in companyCulture" :key="culture.id">
            <div class="culture-icon">{{ culture.icon }}</div>
            <h3 class="culture-title">{{ culture.title }}</h3>
            <p class="culture-desc">{{ culture.desc }}</p>
            <div class="culture-details">
              <div class="detail-item" v-for="detail in culture.details" :key="detail">
                <span class="detail-dot">•</span>
                <span class="detail-text">{{ detail }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 发展历程 -->
    <section class="company-timeline">
      <div class="container">
        <h2 class="section-title">发展历程</h2>
        <p class="section-description">
          从初创到成长，我们始终专注于工业互联网技术创新和产业化应用
        </p>
        <div class="timeline">
          <div class="timeline-item" v-for="milestone in companyTimeline" :key="milestone.id">
            <div class="timeline-year">{{ milestone.year }}</div>
            <div class="timeline-content">
              <div class="timeline-icon">{{ milestone.icon }}</div>
              <h3 class="timeline-title">{{ milestone.title }}</h3>
              <p class="timeline-desc">{{ milestone.desc }}</p>
              <div class="timeline-achievements">
                <div class="achievement-item" v-for="achievement in milestone.achievements" :key="achievement">
                  <span class="achievement-icon">✓</span>
                  <span class="achievement-text">{{ achievement }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 核心团队 -->
    <section class="core-team">
      <div class="container">
        <h2 class="section-title">核心团队</h2>
        <p class="section-description">
          汇聚行业精英，打造专业团队，为客户提供优质服务
        </p>
        <div class="team-grid">
          <div class="team-member" v-for="member in coreTeam" :key="member.id">
            <div class="member-avatar">
              <img :src="member.avatar" :alt="member.name">
            </div>
            <div class="member-info">
              <h3 class="member-name">{{ member.name }}</h3>
              <p class="member-position">{{ member.position }}</p>
              <p class="member-bio">{{ member.bio }}</p>
              <div class="member-expertise">
                <h4>专业领域</h4>
                <div class="expertise-tags">
                  <span class="expertise-tag" v-for="skill in member.expertise" :key="skill">{{ skill }}</span>
                </div>
              </div>
              <div class="member-achievements">
                <h4>主要成就</h4>
                <ul class="achievement-list">
                  <li v-for="achievement in member.achievements" :key="achievement">{{ achievement }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 技术实力 -->
    <section class="technical-strength">
      <div class="container">
        <h2 class="section-title">技术实力</h2>
        <p class="section-description">
          持续技术创新，构建核心竞争优势
        </p>
        <div class="strength-categories">
          <div class="strength-category" v-for="category in technicalStrength" :key="category.id">
            <div class="category-header">
              <div class="category-icon">{{ category.icon }}</div>
              <h3 class="category-title">{{ category.title }}</h3>
              <p class="category-desc">{{ category.desc }}</p>
            </div>
            <div class="category-items">
              <div class="strength-item" v-for="item in category.items" :key="item.name">
                <div class="item-icon">{{ item.icon }}</div>
                <div class="item-content">
                  <h4 class="item-name">{{ item.name }}</h4>
                  <p class="item-desc">{{ item.desc }}</p>
                  <div class="item-progress">
                    <div class="progress-bar">
                      <div class="progress-fill" :style="{width: item.level + '%'}"></div>
                    </div>
                    <span class="progress-text">{{ item.level }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 资质认证 -->
    <section class="qualifications">
      <div class="container">
        <h2 class="section-title">资质认证</h2>
        <p class="section-description">
          获得多项权威认证，彰显企业实力和专业水准
        </p>
        <div class="qualification-tabs">
          <div class="tab-headers">
            <div 
              class="tab-header" 
              v-for="category in qualificationCategories" 
              :key="category.id"
              :class="{active: activeQualification === category.id}"
              @click="activeQualification = category.id"
            >
              {{ category.name }}
            </div>
          </div>
          <div class="tab-content">
            <div class="qualification-grid">
              <div 
                class="qualification-item" 
                v-for="item in qualificationCategories.find(c => c.id === activeQualification).items" 
                :key="item.id"
              >
                <div class="qualification-icon">{{ item.icon }}</div>
                <div class="qualification-info">
                  <h3 class="qualification-name">{{ item.name }}</h3>
                  <p class="qualification-issuer">{{ item.issuer }}</p>
                  <p class="qualification-date">{{ item.date }}</p>
                  <p class="qualification-desc">{{ item.desc }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 合作伙伴 -->
    <section class="partners">
      <div class="container">
        <h2 class="section-title">合作伙伴</h2>
        <p class="section-description">
          与行业领先企业建立战略合作关系，共同推动产业发展
        </p>
        <div class="partner-categories">
          <div class="partner-category" v-for="category in partnerCategories" :key="category.id">
            <h3 class="category-title">{{ category.name }}</h3>
            <div class="partner-grid">
              <div class="partner-item" v-for="partner in category.partners" :key="partner.id">
                <div class="partner-icon">{{ partner.icon }}</div>
                <div class="partner-info">
                  <h4 class="partner-name">{{ partner.name }}</h4>
                  <p class="partner-type">{{ partner.type }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 联系我们 -->
    <section class="contact-us">
      <div class="container">
        <h2 class="section-title">联系我们</h2>
        <p class="section-description">
          期待与您合作，共创美好未来
        </p>
        <div class="contact-content">
          <div class="contact-info">
            <div class="contact-item" v-for="contact in contactInfo" :key="contact.id">
              <div class="contact-icon">{{ contact.icon }}</div>
              <div class="contact-details">
                <h3 class="contact-title">{{ contact.title }}</h3>
                <p class="contact-value">{{ contact.value }}</p>
                <p class="contact-desc">{{ contact.desc }}</p>
              </div>
            </div>
          </div>
          <div class="contact-form">
            <h3>在线咨询</h3>
            <form @submit.prevent="submitForm">
              <div class="form-group">
                <input type="text" v-model="form.name" placeholder="您的姓名" required>
              </div>
              <div class="form-group">
                <input type="tel" v-model="form.phone" placeholder="联系电话" required>
              </div>
              <div class="form-group">
                <input type="email" v-model="form.email" placeholder="邮箱地址">
              </div>
              <div class="form-group">
                <select v-model="form.service" required>
                  <option value="">请选择服务类型</option>
                  <option value="digital">数字化转型</option>
                  <option value="aiot">AIoT平台</option>
                  <option value="carbon">碳管理</option>
                  <option value="equipment">设备管理</option>
                </select>
              </div>
              <div class="form-group">
                <textarea v-model="form.message" placeholder="请描述您的需求" rows="4"></textarea>
              </div>
              <button type="submit" class="submit-btn">提交咨询</button>
            </form>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer组件 -->
    <Footer />
  </div>
</template>

<script>
import UnifiedNavBar from '@/components/UnifiedNavBar.vue'
import Footer from '@/components/Footer.vue'

export default {
  name: 'AboutUs',
  components: {
    UnifiedNavBar,
    Footer
  },
  data() {
    return {
      activeQualification: 1,
      form: {
        name: '',
        phone: '',
        email: '',
        service: '',
        message: ''
      },
      companyStats: [
        { id: 1, value: '6+', label: '年行业经验' },
        { id: 2, value: '200+', label: '服务客户' },
        { id: 3, value: '50+', label: '技术专家' },
        { id: 4, value: '100+', label: '成功项目' }
      ],
      companyCulture: [
        {
          id: 1,
          icon: '💡',
          title: '创新驱动',
          desc: '持续技术创新，引领行业发展',
          details: [
            '鼓励创新思维和实践',
            '投入研发资源持续创新',
            '建立创新激励机制',
            '与高校科研院所合作'
          ]
        },
        {
          id: 2,
          icon: '🎯',
          title: '专业专注',
          desc: '专业的技术团队，专注的服务态度',
          details: [
            '深耕工业互联网领域',
            '打造专业技术团队',
            '提供专业解决方案',
            '持续提升专业能力'
          ]
        },
        {
          id: 3,
          icon: '🤝',
          title: '合作共赢',
          desc: '与客户、合作伙伴携手共创价值',
          details: [
            '建立长期合作关系',
            '实现互利共赢发展',
            '共同推动行业进步',
            '构建生态合作体系'
          ]
        },
        {
          id: 4,
          icon: '🌟',
          title: '追求卓越',
          desc: '追求技术卓越，服务卓越，品质卓越',
          details: [
            '严格的质量管理体系',
            '持续改进和优化',
            '追求客户满意度',
            '打造行业标杆'
          ]
        }
      ],
      companyTimeline: [
        {
          id: 1,
          year: '2018',
          icon: '🚀',
          title: '公司成立',
          desc: '易博数字数字科技有限公司正式成立，专注工业互联网技术研发',
          achievements: [
            '组建核心技术团队',
            '确立发展战略方向',
            '获得天使轮投资',
            '建立研发中心'
          ]
        },
        {
          id: 2,
          year: '2019',
          icon: '🔬',
          title: '技术突破',
          desc: '在工业物联网和人工智能领域取得重要技术突破',
          achievements: [
            '发布首个AIOT平台',
            '获得多项技术专利',
            '通过高新技术企业认定',
            '建立关于我们合作关系'
          ]
        },
        {
          id: 3,
          year: '2020',
          icon: '📈',
          title: '业务拓展',
          desc: '业务快速发展，客户群体不断扩大',
          achievements: [
            '服务客户超过50家',
            '完成A轮融资',
            '设立北京分公司',
            '获得行业认可奖项'
          ]
        },
        {
          id: 4,
          year: '2021',
          icon: '🌐',
          title: '平台升级',
          desc: '全面升级技术平台，推出数字化转型解决方案',
          achievements: [
            '发布2.0版本平台',
            '推出碳管理解决方案',
            '建立全国服务网络',
            '获得ISO质量认证'
          ]
        },
        {
          id: 5,
          year: '2022',
          icon: '🏆',
          title: '行业领先',
          desc: '成为行业领先的工业互联网解决方案提供商',
          achievements: [
            '服务客户超过150家',
            '获得国家级科技奖项',
            '设立上海、广州分公司',
            '完成B轮融资'
          ]
        }
      ],
      coreTeam: [
        {
          id: 1,
          name: '张明华',
          position: 'CEO & 创始人',
          avatar: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Professional%20business%20executive%20CEO%20portrait%20confident%20leader%20corporate%20headshot&image_size=square',
          bio: '清华大学计算机博士，15年工业互联网行业经验，曾任知名科技公司技术总监',
          expertise: ['工业互联网', '人工智能', '企业管理', '战略规划'],
          achievements: [
            '主导多个国家级科技项目',
            '获得20+项技术专利',
            '入选国家千人计划',
            '荣获行业杰出贡献奖'
          ]
        },
        {
          id: 2,
          name: '李雪梅',
          position: 'CTO & 联合创始人',
          avatar: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Professional%20female%20technology%20executive%20CTO%20portrait%20confident%20tech%20leader&image_size=square',
          bio: '北京理工大学软件工程博士，12年技术研发经验，专注于工业物联网技术创新',
          expertise: ['物联网技术', '大数据分析', '云计算', '系统架构'],
          achievements: [
            '主导核心技术平台研发',
            '获得15+项发明专利',
            '发表30+篇学术论文',
            '荣获科技创新奖'
          ]
        },
        {
          id: 3,
          name: '王建国',
          position: 'CPO & 产品总监',
          avatar: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Professional%20product%20manager%20executive%20portrait%20business%20leader%20corporate&image_size=square',
          bio: '华中科技大学工业工程硕士，10年产品管理经验，深谙制造业数字化需求',
          expertise: ['产品设计', '用户体验', '市场分析', '项目管理'],
          achievements: [
            '主导多个成功产品发布',
            '获得产品创新奖',
            '建立完善产品体系',
            '客户满意度95%+'
          ]
        },
        {
          id: 4,
          name: '陈志强',
          position: 'CMO & 市场总监',
          avatar: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Professional%20marketing%20executive%20CMO%20portrait%20business%20development%20leader&image_size=square',
          bio: '上海交通大学MBA，8年市场营销经验，擅长品牌建设和市场拓展',
          expertise: ['品牌营销', '渠道建设', '商务拓展', '客户关系'],
          achievements: [
            '建立全国销售网络',
            '品牌知名度提升300%',
            '客户数量增长500%',
            '获得营销创新奖'
          ]
        }
      ],
      technicalStrength: [
        {
          id: 1,
          icon: '🔧',
          title: '核心技术',
          desc: '自主研发的核心技术平台和算法',
          items: [
            {
              icon: '🌐',
              name: '工业物联网',
              desc: '设备连接、数据采集、协议转换',
              level: 92
            },
            {
              icon: '📊',
              name: '大数据分析',
              desc: '实时分析、预测建模、智能决策',
              level: 88
            },
            {
              icon: '☁️',
              name: '云计算平台',
              desc: '弹性扩展、高可用、微服务架构',
              level: 95
            },
            {
              icon: '🤖',
              name: '人工智能',
              desc: '机器学习、深度学习、智能算法',
              level: 90
            },
            {
              icon: '🔐',
              name: '信息安全',
              desc: '数据加密、访问控制、安全审计',
              level: 87
            }
          ]
        },
        {
          id: 2,
          icon: '💻',
          title: '技术平台',
          desc: '完整的技术平台和开发工具链',
          items: [
            {
              icon: '🏗️',
              name: '开发平台',
              desc: '低代码开发、快速部署、可视化配置',
              level: 93
            },
            {
              icon: '📱',
              name: '移动应用',
              desc: 'APP开发、响应式设计、跨平台支持',
              level: 89
            },
            {
              icon: '🖥️',
              name: 'Web应用',
              desc: '前端框架、用户界面、交互设计',
              level: 91
            },
            {
              icon: '🔌',
              name: 'API接口',
              desc: 'RESTful API、GraphQL、SDK开发',
              level: 94
            }
          ]
        },
        {
          id: 3,
          icon: '🛡️',
          title: '安全保障',
          desc: '全方位的信息安全和数据保护能力',
          items: [
            {
              icon: '🔐',
              name: '数据安全',
              desc: '加密传输、安全存储、访问控制',
              level: 94
            },
            {
              icon: '🛡️',
              name: '网络安全',
              desc: '防火墙、入侵检测、安全审计',
              level: 92
            },
            {
              icon: '🔍',
              name: '安全监控',
              desc: '实时监控、异常检测、应急响应',
              level: 90
            },
            {
              icon: '📋',
              name: '合规认证',
              desc: '等保认证、ISO27001、GDPR合规',
              level: 88
            }
          ]
        }
      ],
      qualificationCategories: [
        {
          id: 1,
          name: '企业资质',
          items: [
            {
              id: 1,
              icon: '🏆',
              name: '高新技术企业',
              issuer: '科技部',
              date: '2019年',
              desc: '通过国家高新技术企业认定'
            },
            {
              id: 2,
              icon: '💼',
              name: '软件企业认证',
              issuer: '工信部',
              date: '2020年',
              desc: '获得软件企业认证，具备软件开发资质'
            },
            {
              id: 3,
              icon: '📜',
              name: 'ISO9001质量认证',
              issuer: 'ISO国际标准组织',
              date: '2021年',
              desc: '通过ISO9001质量管理体系认证'
            }
          ]
        },
        {
          id: 2,
          name: '荣誉奖项',
          items: [
            {
              id: 1,
              icon: '🥇',
              name: '科技进步奖',
              issuer: '深圳市政府',
              date: '2021年',
              desc: '获得深圳市科技进步一等奖'
            },
            {
              id: 2,
              icon: '🏅',
              name: '创新企业奖',
              issuer: '广东省政府',
              date: '2022年',
              desc: '入选广东省创新型企业'
            },
            {
              id: 3,
              icon: '⭐',
              name: '科技创新奖',
              issuer: '深圳市科创委',
              date: '2023年',
              desc: '获得深圳市科技创新奖'
            },
            {
              id: 4,
              icon: '🚀',
              name: '独角兽企业',
              issuer: '深圳市政府',
              date: '2024年',
              desc: '入选深圳市独角兽企业名单'
            }
          ]
        },
        {
          id: 3,
          name: '知识产权',
          items: [
            {
              id: 1,
              icon: '📋',
              name: '发明专利',
              issuer: '国家知识产权局',
              date: '2019-2024年',
              desc: '获得35项发明专利授权'
            },
            {
              id: 2,
              icon: '🔧',
              name: '实用新型专利',
              issuer: '国家知识产权局',
              date: '2018-2024年',
              desc: '获得28项实用新型专利'
            },
            {
              id: 3,
              icon: '🎨',
              name: '外观设计专利',
              issuer: '国家知识产权局',
              date: '2020-2024年',
              desc: '获得15项外观设计专利'
            },
            {
              id: 4,
              icon: '©️',
              name: '软件著作权',
              issuer: '国家版权局',
              date: '2018-2024年',
              desc: '获得50+项软件著作权'
            }
          ]
        }
      ],
      partnerCategories: [
        {
          id: 1,
          name: '战略合作伙伴',
          partners: [
            { id: 1, icon: '🏢', name: '华为技术', type: '技术合作' },
            { id: 2, icon: '🔵', name: '阿里云', type: '云服务合作' },
            { id: 3, icon: '🟦', name: '腾讯云', type: '云服务合作' },
            { id: 4, icon: '🏭', name: '富士康', type: '产业合作' }
          ]
        },
        {
          id: 2,
          name: '技术合作伙伴',
          partners: [
            { id: 1, icon: '🎓', name: '清华大学', type: '关于我们合作' },
            { id: 2, icon: '🎓', name: '北京理工大学', type: '技术研发' },
            { id: 3, icon: '🔬', name: '中科院自动化所', type: '科研合作' },
            { id: 4, icon: '🏛️', name: '工信部电子五所', type: '标准制定' }
          ]
        }
      ],
      contactInfo: [
        {
          id: 1,
          icon: '📍',
          title: '公司地址',
          value: '济南市山东人工智能产业园R2-A栋15楼',
          desc: '欢迎莅临参观交流'
        },
        {
          id: 2,
          icon: '📞',
          title: '联系电话',
          value: '186xxxxxxxxx',
          desc: '7×24小时服务热线'
        },
        {
          id: 3,
          icon: '✉️',
          title: '邮箱地址',
          value: 'contact@yibotech.com',
          desc: '商务合作咨询邮箱'
        }
      ]
    }
  },
  methods: {
    submitForm() {
      // 表单提交逻辑
      console.log('提交表单:', this.form)
      // 这里可以添加实际的表单提交逻辑
      alert('感谢您的咨询，我们会尽快与您联系！')
      // 重置表单
      this.form = {
        name: '',
        phone: '',
        email: '',
        service: '',
        message: ''
      }
    }
  }
}
</script>

<style scoped>
.about-us {
  min-height: 100vh;
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 120px 0 80px;
  text-align: center;
}

.page-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.page-subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

/* 公司简介 */
.company-intro {
  padding: 80px 0;
  background: #f8f9fa;
}

.intro-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.intro-text .section-title {
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 2rem;
  font-weight: 700;
}

.intro-description {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #555;
  margin-bottom: 1.5rem;
}

.company-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-top: 40px;
}

.stat-item {
  text-align: center;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.stat-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 1rem;
  color: #666;
}

.intro-image img {
  width: 100%;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
}

/* 企业文化 */
.company-culture {
  padding: 80px 0;
}

.section-title {
  font-size: 2.5rem;
  text-align: center;
  color: #2c3e50;
  margin-bottom: 1rem;
  font-weight: 700;
}

.section-description {
  text-align: center;
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.culture-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.culture-card {
  background: white;
  padding: 40px 30px;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.culture-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0,0,0,0.15);
}

.culture-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.culture-title {
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 1rem;
  font-weight: 600;
}

.culture-desc {
  font-size: 1rem;
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.culture-details {
  text-align: left;
}

.detail-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.detail-dot {
  color: #667eea;
  margin-right: 0.5rem;
  font-weight: bold;
}

.detail-text {
  font-size: 0.9rem;
  color: #555;
}

/* 发展历程 */
.company-timeline {
  padding: 80px 0;
  background: #f8f9fa;
}

.timeline {
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #667eea;
  transform: translateX(-50%);
}

.timeline-item {
  display: flex;
  margin-bottom: 60px;
  position: relative;
}

.timeline-item:nth-child(odd) {
  flex-direction: row;
}

.timeline-item:nth-child(even) {
  flex-direction: row-reverse;
}

.timeline-year {
  width: 100px;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 700;
  color: #667eea;
  background: white;
  padding: 10px;
  border-radius: 50px;
  border: 3px solid #667eea;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
}

.timeline-content {
  flex: 1;
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
  margin: 0 30px;
  position: relative;
}

.timeline-icon {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.timeline-title {
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 1rem;
  font-weight: 500;
}

.timeline-desc {
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.timeline-achievements {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.achievement-item {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: #555;
}

.achievement-icon {
  color: #28a745;
  margin-right: 0.5rem;
  font-weight: bold;
}

/* 核心团队 */
.core-team {
  padding: 80px 0;
}

.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 40px;
}

.team-member {
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.team-member:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0,0,0,0.15);
}

.member-avatar {
  height: 200px;
  overflow: hidden;
}

.member-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.member-info {
  padding: 30px;
}

.member-name {
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.member-position {
  font-size: 1rem;
  color: #667eea;
  margin-bottom: 1rem;
  font-weight: 500;
}

.member-bio {
  font-size: 0.9rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.member-expertise h4,
.member-achievements h4 {
  font-size: 1rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.expertise-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 1.5rem;
}

.expertise-tag {
  background: #f0f2ff;
  color: #667eea;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.achievement-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.achievement-list li {
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 0.5rem;
  padding-left: 1rem;
  position: relative;
}

.achievement-list li::before {
  content: '•';
  color: #28a745;
  position: absolute;
  left: 0;
  font-weight: bold;
}

/* 技术实力 */
.technical-strength {
  padding: 80px 0;
  background: #f8f9fa;
}

.strength-categories {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 40px;
}

.strength-category {
  background: white;
  border-radius: 15px;
  padding: 40px 30px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
}

.category-header {
  text-align: center;
  margin-bottom: 2rem;
}

.category-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.category-title {
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.category-desc {
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
}

.strength-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.strength-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.item-icon {
  font-size: 2rem;
  margin-right: 20px;
  margin-top: 5px;
}

.item-content {
  flex: 1;
}

.item-name {
  font-size: 1.1rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.item-desc {
  font-size: 0.9rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.item-progress {
  display: flex;
  align-items: center;
  gap: 10px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #f0f2ff;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.9rem;
  color: #667eea;
  font-weight: 600;
  min-width: 40px;
}

/* 资质认证 */
.qualifications {
  padding: 80px 0;
}

.qualification-tabs {
  max-width: 1000px;
  margin: 0 auto;
}

.tab-headers {
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  border-bottom: 2px solid #f0f2ff;
}

.tab-header {
  padding: 15px 30px;
  font-size: 1.1rem;
  color: #666;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.3s ease;
}

.tab-header.active {
  color: #667eea;
  border-bottom-color: #667eea;
  font-weight: 600;
}

.qualification-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.qualification-item {
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.qualification-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0,0,0,0.15);
}

.qualification-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.qualification-name {
  font-size: 1.3rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.qualification-issuer {
  font-size: 1rem;
  color: #667eea;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.qualification-date {
  font-size: 0.9rem;
  color: #999;
  margin-bottom: 1rem;
}

.qualification-desc {
  font-size: 0.9rem;
  color: #666;
  line-height: 1.6;
}

/* 合作伙伴 */
.partners {
  padding: 80px 0;
  background: #f8f9fa;
}

.partner-categories {
  display: grid;
  gap: 40px;
}

.partner-category .category-title {
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 600;
}

.partner-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.partner-item {
  background: white;
  padding: 30px 20px;
  border-radius: 10px;
  box-shadow: 0 3px 15px rgba(0,0,0,0.1);
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.partner-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

.partner-icon {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.partner-name {
  font-size: 1.1rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.partner-type {
  font-size: 0.9rem;
  color: #667eea;
  font-weight: 500;
}

/* 联系我们 */
.contact-us {
  padding: 80px 0;
}

.contact-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;
}

.contact-info {
  display: grid;
  gap: 30px;
}

.contact-item {
  display: flex;
  align-items: flex-start;
  padding: 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
}

.contact-icon {
  font-size: 2.5rem;
  margin-right: 20px;
  margin-top: 5px;
}

.contact-title {
  font-size: 1.2rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.contact-value {
  font-size: 1.1rem;
  color: #667eea;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.contact-desc {
  font-size: 0.9rem;
  color: #666;
  line-height: 1.6;
}

.contact-form {
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
}

.contact-form h3 {
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 600;
}

.form-group {
  margin-bottom: 20px;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #f0f2ff;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
}

.submit-btn {
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .intro-content,
  .contact-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  
  .timeline::before {
    left: 30px;
  }
  
  .timeline-item {
    flex-direction: column;
    padding-left: 60px;
  }
  
  .timeline-year {
    left: 30px;
  }
  
  .timeline-content {
    margin: 0;
  }
  
  .team-grid,
  .strength-categories {
    grid-template-columns: 1fr;
  }
  
  .tab-headers {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
  
  .tab-header {
    padding: 10px 20px;
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 2rem;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .company-stats {
    grid-template-columns: 1fr;
  }
  
  .culture-grid,
  .qualification-grid,
  .partner-grid {
    grid-template-columns: 1fr;
  }
  
  .timeline-achievements {
    grid-template-columns: 1fr;
  }
}
</style>